// noticer.ts — the imperative narration channel for the Noticer (StatusCorner).
// The onboarding conductor uses this to make the corner *speak*: a sticky line
// with optional action buttons ("next →", "skip"). It rides the same window-event
// grammar as the existing `innernet:clip` flashes, so nothing else changes.
//
//   narrate({ id, text, actions: [{ label: "next →", act: "next" }], sticky: true })
//   narrateClear()   // hand the corner back to hover-hints
//
// A clicked action re-emits `innernet:narrate-act` with its `act` id; the
// conductor listens for that to advance. Kept tiny + framework-free so both the
// dispatcher (conductor) and the receiver (StatusCorner) share one contract.

export interface NoticerAction {
  label: string;
  /** semantic id the conductor interprets (next · skip · goto:/x · unlock:x · create · import · email). */
  act: string;
}

export interface NarrateDetail {
  /** stable id for the current line (so repeat dispatches of the same beat no-op). */
  id: string;
  text: string;
  actions?: NoticerAction[];
  /** sticky lines stay until cleared or replaced (default true for narration). */
  sticky?: boolean;
  /** a prompt the line hands over, shown under it with its own copy (the guide
   *  answers a copy with `copied:<key>` on the act channel). */
  prompt?: { key: string; text: string };
}

export const NARRATE_EVENT = "innernet:narrate";
export const NARRATE_ACT_EVENT = "innernet:narrate-act";

// The line currently being spoken. Events alone lose the first beat when a
// receiver mounts in the same commit the speaker fires (effects run in tree
// order — the conductor speaks before a freshly-mounted guide can listen), so
// the channel remembers what is being said and a late subscriber reads it.
//
// ASIDES (2026-10-22). A passing line (`sticky: false`, nothing to press: a
// space's or a view's first-look intro) is said, never kept as the line being
// spoken, and a timed clear clears only its own line. Before this, an intro said
// while a tour was asking "want me to show you round?" took that line's chips
// away, and its timer then cleared the tour too: the conversation stopped with
// no way on.
let current: NarrateDetail | null = null;

/** a passing line: said, but it never stands in for the line that asks */
export function isAside(d: NarrateDetail | null | undefined): boolean {
  return !!d && d.sticky === false && !(d.actions && d.actions.length);
}

/** What the channel is saying right now — for receivers that mounted late. */
export function currentNarration(): NarrateDetail | null {
  return current;
}

export function narrate(detail: NarrateDetail) {
  if (typeof window === "undefined") return;
  if (!isAside(detail)) current = detail;
  window.dispatchEvent(new CustomEvent(NARRATE_EVENT, { detail }));
}

/** Clear the narration and return the corner to hover-hint duty. With an id,
 *  only that line goes: a line someone else is speaking now stands (receivers
 *  read `currentNarration()` on a clear and keep it). */
export function narrateClear(id?: string) {
  if (typeof window === "undefined") return;
  if (!id || current?.id === id) current = null;
  window.dispatchEvent(new CustomEvent(NARRATE_EVENT, { detail: null }));
}

/** Fire an action the user clicked in the corner (or an auto-advance). */
export function narrateAct(act: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(NARRATE_ACT_EVENT, { detail: { act } }));
}
